import React from "react";
import { Tooltip } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import { TFunction } from "i18next";

import IconInfo from "~/components/Icons/info";
import { getCandidateTypeLabel } from "~/pages/CatalogApplications/model/candidateTypeLabels";
import { FormValues } from "~/pages/CatalogApplications/types";
import { DefaultTextCell } from "~/shared/ui/TableV2/ui/Cells";

import StatusUpdatePopover from "./StatusUpdatePopover";
import {
  getAuditStatusPopoverItems,
  getStatusButtonClassName,
  getStatusPopoverItems,
} from "./candidateStatusUi";
import {
  getTerritorialStatus,
  TERRITORIAL_STATUS_TEXTS,
  TerritorialStatusKind,
} from "./territorialStatus";
import { TerritorialStatusIndicator } from "./TerritorialStatusIndicator";

import ta from "./tableAdapterStyles.less";
import s from "~/pages/CatalogApplications/CatalogApplications.less";

const TERRITORIAL_STATUS_EMOJI: Record<TerritorialStatusKind, string> = {
  red: "🔴",
  orange: "🟠",
  green: "🟢",
  blue: "🔵",
  purple: "🟣",
};

const TERRITORIAL_RULE_LINES = (
  Object.keys(TERRITORIAL_STATUS_EMOJI) as TerritorialStatusKind[]
).map(
  (kind) =>
    `${TERRITORIAL_STATUS_EMOJI[kind]} – ${TERRITORIAL_STATUS_TEXTS[kind]}`,
);

const statusButtonStyles = {
  btnNew: s.btnNew,
  btnNewBlue: s.btnNewBlue,
  btnNewPurple: s.btnNewPurple,
};

const TerritorialTooltipContent = () => (
  <div className={ta.territorialTooltipWrap}>
    <p className={ta.territorialTooltipIntro}>
      Справочная информация о территориальных статусах
    </p>
    <ul className={ta.territorialTooltipList}>
      {TERRITORIAL_RULE_LINES.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  </div>
);

const territorialTooltipProps = {
  placement: "top" as const,
  arrow: false,
  classNames: {
    root: ta.territorialTooltipRoot,
    body: ta.territorialTooltipBody,
  },
  styles: {
    root: {
      backgroundColor: "transparent",
      boxShadow: "none",
    },
    body: {
      backgroundColor: "#fff",
      color: "#495964",
    },
  },
};

type UpdateStatus = (v: { status: string; candidateId: string }) => void;
type UpdateAudit = (v: { auditStatus: string; candidateId: string }) => void;
const buildDistanceColumn = (
  t: TFunction,
  option: string,
): ColumnType<FormValues> => ({
  title: (
    <span className={ta.distanceHeaderWrap}>
      <span>{t(`catalog_applications.${option}`)}</span>
      <Tooltip
        title={<TerritorialTooltipContent />}
        {...territorialTooltipProps}
      >
        <span className={ta.distanceIconTrigger}>
          <IconInfo width="12" height="12" fill="#495964" />
        </span>
      </Tooltip>
    </span>
  ),
  align: "center",
  dataIndex: "distanceToNearestDealer",
  key: option,
  render: (_value, record) => {
    const status = getTerritorialStatus(
      record.distanceToNearestDealer,
      record.candidateType,
      record.isHolding,
    );
    if (!status) {
      return <div className={ta.distanceTrafficCell} />;
    }
    return (
      <div className={ta.distanceTrafficCell}>
        <TerritorialStatusIndicator status={status} />
      </div>
    );
  },
});

const buildStatusColumn = (
  title: React.ReactNode,
  option: string,
  hasPermission: (permission: string) => boolean,
  updateStatus: UpdateStatus,
): ColumnType<FormValues> => ({
  title,
  dataIndex: "status",
  key: option,
  render: (_: unknown, record: FormValues) => (
    <div onClick={(e) => e.stopPropagation()}>
      <div
        className={getStatusButtonClassName(record.status, statusButtonStyles)}
      >
        {record.status}
      </div>
      <StatusUpdatePopover
        allowed={hasPermission("candidateSelection.status.update")}
        items={getStatusPopoverItems()}
        direction="left"
        selectedValue={record.status}
        onSelect={(value) => {
          updateStatus({ status: value, candidateId: record.id });
        }}
        triggerClassName={s.changeStatus}
      />
    </div>
  ),
});

const buildAuditColumn = (
  title: React.ReactNode,
  option: string,
  hasPermission: (permission: string) => boolean,
  updateAudit: UpdateAudit,
): ColumnType<FormValues> => ({
  title,
  dataIndex: "auditStatus",
  key: option,
  render: (_: unknown, record: FormValues) => (
    <div onClick={(e) => e.stopPropagation()}>
      <div className={s.btnNew}>{record.auditStatus}</div>
      <StatusUpdatePopover
        allowed={hasPermission("candidateSelection.audit.status.update")}
        items={getAuditStatusPopoverItems()}
        direction="right"
        selectedValue={record.auditStatus}
        onSelect={(value) => {
          updateAudit({ auditStatus: value, candidateId: record.id });
        }}
        triggerClassName={s.changeStatus}
      />
    </div>
  ),
});

const buildDefaultColumn = (
  title: React.ReactNode,
  option: string,
): ColumnType<FormValues> => ({
  title,
  dataIndex: option,
  key: option,
  render: (text: unknown) => <DefaultTextCell text={text} />,
});

const buildCandidateTypeColumn = (
  t: TFunction,
  title: React.ReactNode,
  option: string,
): ColumnType<FormValues> => ({
  title,
  dataIndex: "candidateType",
  key: option,
  render: (value: unknown) => (
    <DefaultTextCell
      text={getCandidateTypeLabel(t, value as string | null | undefined)}
    />
  ),
});

const buildIsCompetitiveColumn = (
  t: TFunction,
  title: React.ReactNode,
  option: string,
): ColumnType<FormValues> => ({
  title,
  dataIndex: "isCompetitive",
  key: option,
  render: (value: unknown) => (
    <DefaultTextCell
      text={
        typeof value === "boolean"
          ? t(
              value
                ? "catalog_applications.competitive"
                : "catalog_applications.notCompetitive",
            )
          : ""
      }
    />
  ),
});

export const getCatalogApplicationsColumns = (
  t: TFunction,
  options: string[],
  hasPermission: (permission: string) => boolean,
  updateStatus: UpdateStatus,
  updateAudit: UpdateAudit,
): ColumnsType<FormValues> =>
  options.map((option) => {
    const title = t(`catalog_applications.${option}`);

    switch (option) {
      case "distanceToNearestDealer":
        return buildDistanceColumn(t, option);
      case "status":
        return buildStatusColumn(title, option, hasPermission, updateStatus);
      case "audit":
        return buildAuditColumn(title, option, hasPermission, updateAudit);
      case "candidateType":
        return buildCandidateTypeColumn(t, title, option);
      case "isCompetitive":
        return buildIsCompetitiveColumn(t, title, option);
      default:
        return buildDefaultColumn(title, option);
    }
  });
